import { isEmpty, isNull, omit } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { SiteFilterKeys, TSiteFilterKey } from '../../../domain/saved-filters/interface';
import { ISite } from '../../../domain/site/interface';
import { ISiteSanSelectOption } from '../../energyUsageData/sidebar/context/EnergyUsageSidebarFiltersContext';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { ISitesPageState, ISitesSidebarFilters } from './SitesSidebarFiltersContext';

export const convertSitesPageStateToFilterState = (pageState: ISitesPageState): ISitesSidebarFilters => {
    const result: ISitesSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert control_provider_id
    if (filterData.control_provider_id !== undefined) {
        result.control_provider_id = Array.isArray(filterData.control_provider_id)
            ? filterData.control_provider_id
            : [filterData.control_provider_id];
    }

    // Convert utility_customer_id
    if (filterData.utility_customer_id !== undefined) {
        result.utility_customer_id = Array.isArray(filterData.utility_customer_id)
            ? filterData.utility_customer_id
            : [filterData.utility_customer_id];
    }

    // Convert program_id
    if (filterData.program_id !== undefined) {
        result.program_id = Array.isArray(filterData.program_id) ? filterData.program_id : [filterData.program_id];
    }

    // Convert site_utility
    if (filterData.site_utility !== undefined) {
        result.site_utility = Array.isArray(filterData.site_utility) ? filterData.site_utility : [filterData.site_utility];
    }

    // Convert site_status
    if (filterData.status !== undefined) {
        result.status = Array.isArray(filterData.status) ? filterData.status : [filterData.status];
    }

    // Convert market_zone (handle as array even though it might be a single value)
    if (filterData.market !== undefined) {
        result.market = Array.isArray(filterData.market) ? filterData.market : [filterData.market];
    }

    // Convert dispatch_type
    if (filterData.dispatch_type !== undefined) {
        result.dispatch_type = filterData.dispatch_type;
    }

    // Convert hasVen
    if (filterData.hasVen !== undefined) {
        result.hasVen = filterData.hasVen;
    }

    if (filterData.interval_data !== undefined) {
        result.interval_data = filterData.interval_data;
    }

    if (filterData.exist_salesforce_meter_enrollments !== undefined) {
        result.exist_salesforce_meter_enrollments = filterData.exist_salesforce_meter_enrollments;
    }

    if (filterData.exist_salesforce_meters !== undefined) {
        result.exist_salesforce_meters = filterData.exist_salesforce_meters;
    }

    if (filterData.isClosed !== undefined) {
        result.isClosed = filterData.isClosed;
    }

    if (filterData.resource_telemetry_status !== undefined) {
        result.resource_telemetry_status = filterData.resource_telemetry_status;
    }

    return result;
};

export const transformSitesSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TSiteFilterKey, string> => {
    let result: Record<TSiteFilterKey, string> = {} as Record<TSiteFilterKey, string>;

    console.log('filter');
    console.log(filter);

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case SiteFilterKeys.controlProviderId:
                result[key] = value
                    .map((val: string) => {
                        const option = context.controlProviderOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.utilityCustomerId:
                result[key] = value
                    .map((val: string) => {
                        const option = context.utilityCustomerOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.programId:
                result[key] = value
                    .map((val: string) => {
                        const option = context.programOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.utility:
                result[key] = value.join(', ');
                break;
            case SiteFilterKeys.market:
                result[key] = value
                    .map((val: string) => {
                        const option = context.marketOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.status:
                result[key] = value
                    .map((val: string) => {
                        const option = context.siteStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.caisoResource:
                result[key] = value
                    .map((val: string) => {
                        const option = context.caisoResourceOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.dispatchType:
                result[key] = context.dispatchTypeOptions.find(option => option.value === value)?.label || '';
                break;
            case SiteFilterKeys.hasVen:
                result[key] = context.hasVenOptions.find(option => option.value === value)?.label || '';
                break;
            case SiteFilterKeys.resourceTelemetryStatus:
                result[key] = value
                    .map((val: string) => {
                        const option = context.resourceTelemetryStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case SiteFilterKeys.intervalData:
                result[key] = context.siteIntervalDataOptions.find(option => option.value === value)?.label || '';
                break;
            case SiteFilterKeys.existSalesforceMeterEnrollments:
                result[key] = context.meterEnrollmentOptions.find(option => option.value === value)?.label || '';
                break;
            case SiteFilterKeys.existSalesforceMeters:
                result[key] = context.meterOptions.find(option => option.value === value)?.label || '';
                break;
            case SiteFilterKeys.isClosed:
                result[key] = context.closedSitesOptions.find(option => option.value === value)?.label || '';
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TSiteFilterKey, string>;
    }

    return result;
};

export const transformSitesSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TSiteFilterKey, unknown> => {
    const result: Record<TSiteFilterKey, unknown> = {} as Record<TSiteFilterKey, unknown>;

    // I need to convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case SiteFilterKeys.controlProviderId:
            case SiteFilterKeys.utilityCustomerId:
            case SiteFilterKeys.programId:
            case SiteFilterKeys.utility:
            case SiteFilterKeys.dispatchType:
            case SiteFilterKeys.intervalData:
            case SiteFilterKeys.market:
            case SiteFilterKeys.status:
            case SiteFilterKeys.caisoResource:
            case SiteFilterKeys.resourceTelemetryStatus:
                result[key] = value;
                break;

            case SiteFilterKeys.isClosed:
            case SiteFilterKeys.existSalesforceMeterEnrollments:
            case SiteFilterKeys.existSalesforceMeters:
            case SiteFilterKeys.hasVen:
                result[key] = String(value);
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformSitesSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TSiteFilterKey, unknown> => {
    const result: Record<TSiteFilterKey, unknown> = {} as Record<TSiteFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case SiteFilterKeys.controlProviderId:
            case SiteFilterKeys.utilityCustomerId:
            case SiteFilterKeys.programId:
            case SiteFilterKeys.utility:
            case SiteFilterKeys.dispatchType:
            case SiteFilterKeys.intervalData:
            case SiteFilterKeys.market:
            case SiteFilterKeys.status:
            case SiteFilterKeys.caisoResource:
            case SiteFilterKeys.resourceTelemetryStatus:
                result[key] = value;
                break;

            case SiteFilterKeys.isClosed:
            case SiteFilterKeys.existSalesforceMeterEnrollments:
            case SiteFilterKeys.existSalesforceMeters:
            case SiteFilterKeys.hasVen:
                result[key] = typeof value === 'string' ? value === 'true' : undefined;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case 'control_provider_id':
                if (Array.isArray(value)) {
                    const validOptionValues = context.controlProviderOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'utility_customer_id':
                if (Array.isArray(value)) {
                    const validOptionValues = context.utilityCustomerOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'program_id':
                if (Array.isArray(value)) {
                    const validOptionValues = context.programOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'caisoResource':
                if (Array.isArray(value)) {
                    const validOptionValues = context.caisoResourceOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'market':
                if (Array.isArray(value)) {
                    const validOptionValues = context.marketOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'status':
                if (Array.isArray(value)) {
                    const validOptionValues = context.siteStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'dispatch_type':
                if (typeof value === 'string' && !context.dispatchTypeOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;

            case 'hasVen':
                if (typeof value === 'string' && !context.hasVenOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;

            case 'resource_telemetry_status':
                if (Array.isArray(value)) {
                    const validOptionValues = context.resourceTelemetryStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;

            case 'interval_data':
                if (typeof value === 'string' && !context.siteIntervalDataOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;

            case 'exist_salesforce_meter_enrollments':
                if (typeof value === 'string' && !context.meterEnrollmentOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;

            case 'exist_salesforce_meters':
                if (typeof value === 'string' && !context.meterOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

export function parseSiteSans(siteSansParam: string | string[] | undefined): string[] {
    if (!siteSansParam) return [];

    return Array.isArray(siteSansParam) ? siteSansParam.map(item => item.toString()) : [siteSansParam.toString()];
}

export function siteToSelectOption(site: ISite): ISiteSanSelectOption {
    const siteSans = site.sans || [];
    return {
        site: site,
        sans: siteSans.map(san => ({ label: san.service_account_number, value: san.service_account_number })),
        values: siteSans.map(san => san.service_account_number),
    };
}

function getEmptyFilterProperties(obj: Record<TSiteFilterKey, string>): TSiteFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TSiteFilterKey[];
}
